import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Segment } from '~src/interfaces/segment.interface';

import AvatarBox from '~src/shared/avatarBox/AvatarBox';
import { getNameOfSegment } from '~src/services/getNameOfSegment';
import CustomChips from '~src/shared/form/customChips/CustomChips';
import { translateError } from '~src/services/translationServices';
import { ErrorMessage } from '~src/shared/form/inputs/inputs.sc';
import { fetchSearchSegments } from '~src/services/fetch/fetchSearchSegments.service';
import { FetchSearchSuggestions } from '~src/shared/form/select/SelectSearch/selectSearch.interfaces';

import { ChipsContainer, ListName, SuggestionsListItem } from './PeopleChips.sc';
import CustomChip, { HasSegmentRemoveHandler } from './CustomChip';

interface Props {
  error?: boolean;
  selectedPeople?: Segment[];
  setInputRef?: ((node: HTMLInputElement) => void) | null;
  placeHolderKey?: string;
  fetchSuggestions?: FetchSearchSuggestions<Segment[]>;
  minValue?: number;
  chipsName: string;
  onChangePeople: (chips: Segment[], name: string) => void;
  deleteError?: (name: string) => void;
  // TODO change to the real type
  onFocus?: (e: unknown) => void;
  // TODO change to the real type
  onBlur?: (e: unknown) => void;
  errorTxt?: string;
  locked?: boolean;
  filter?: string;
  blank?: boolean;
  excludeSegmentIds?: string[];
  noneRemovableIds?: string[];
}

const PeopleChips: FC<Props> = (props) => {
  const [isFocused, setIsFocused] = useState(false);
  const [chips, setChips] = useState(props.selectedPeople || []);
  const [error, setError] = useState(false);
  const [t] = useTranslation();

  useEffect(() => {
    setChips(props.selectedPeople || []);
  }, [props.selectedPeople]);

  const onChange = (tmpChips: Segment[]) => {
    // deleting duplicates in new Chipsarray
    if (tmpChips && (!props.minValue || tmpChips.length >= props.minValue)) {
      const uniqueChips = tmpChips.filter(
        (chip, index, self) => index === self.findIndex((segment) => segment.id === chip.id),
      );
      props.onChangePeople(uniqueChips, props.chipsName);
      setChips(uniqueChips);
      if (uniqueChips.length > 0) {
        setError(false);
        if (props.deleteError) {
          props.deleteError(props.chipsName);
        }
      }
    }
  };

  const renderSuggestionsListItem = (
    selected: boolean,
    value: Segment,
    handleSelect: (val: Segment) => void,
  ) => {
    let isThere = false;
    if (chips) {
      isThere = chips.filter((chip) => chip.id === value.id).length > 0;
    }
    const selectThis = () => {
      handleSelect(value);
    };
    return (
      <SuggestionsListItem key={value.id} $isThere={isThere} $selected={selected} onClick={selectThis}>
        <AvatarBox type={value.type} name={getNameOfSegment(t, value)} url={value.image_url} size={35} />
        <ListName>{getNameOfSegment(t, value)}</ListName>
      </SuggestionsListItem>
    );
  };

  const renderChip = (value: Segment & HasSegmentRemoveHandler) => (
    <CustomChip
      key={value.id}
      value={value}
      locked={
        props.locked ||
        (!!props.minValue && chips.length <= props.minValue) ||
        props.noneRemovableIds?.includes(value.id)
      }
      tooltipText={
        props.noneRemovableIds?.includes(value.id)
          ? t('x.questions.card.selection_card.tooltip.not-deletable')
          : ''
      }
    />
  );

  // TODO change to the real type
  const onFocus = (e: unknown) => {
    if (props.onFocus) {
      props.onFocus(e);
    }
    setIsFocused(true);
  };

  // TODO change to the real type
  const onBlur = (e: unknown) => {
    if (props.onBlur) {
      props.onBlur(e);
    }
    setIsFocused(false);
  };

  const inputPlaceholder = {
    value: t(props.placeHolderKey || 'x.surveys.schedule.input.placeholder'),
    key: props.placeHolderKey || 'x.surveys.schedule.input.placeholder',
  };
  const emptyMessage = {
    value: t('x.sidebar.search.nothing_found.message'),
    key: 'x.sidebar.search.nothing_found.message',
  };

  return (
    <>
      <ChipsContainer
        $blank={props.blank}
        $locked={props.locked}
        $error={((props.error || error) && chips.length < 1) || props.errorTxt}
        onFocus={onFocus}
        onBlur={onBlur}
        $isFocused={isFocused}
      >
        <CustomChips
          blank={props.blank}
          error={((props.error || error) && chips.length < 1) || !!props.errorTxt}
          isFocused={isFocused}
          locked={props.locked}
          setInputRef={props.setInputRef || (() => null)}
          inputPlaceholder={inputPlaceholder}
          fetchSearchSuggestions={props.fetchSuggestions || fetchSearchSegments}
          emptyMessage={emptyMessage}
          onChange={onChange}
          renderChip={renderChip}
          chipsData={chips}
          renderItem={renderSuggestionsListItem}
          filter={props.filter}
          excludeSegmentIds={props.excludeSegmentIds}
        />
      </ChipsContainer>
      {props.errorTxt && (
        <ErrorMessage>{translateError(t, props.errorTxt, { field: props.chipsName })}</ErrorMessage>
      )}
    </>
  );
};

export default React.memo(PeopleChips);
